// src/fleet-stats/components/HeadlineSentence.jsx
// The page's hero: one big takeaway sentence + a muted context strip.
// Emphasis discipline: numbers are semibold and carry any state color; the
// words around them stay quiet. Consecutive segments sharing an `action` are
// wrapped in one button with a subtle hover underline.
import React from "react";
import { C } from "../constants.js";

const TONE_COLOR = {
    plain: C.grey1,
    muted: C.grey2,
    value: C.offWhite,
    good: C.neonGreen,
    warn: C.amber,
    crit: C.red,
};

const EMPHASIZED = new Set(["value", "good", "warn", "crit"]);

function Seg({ seg }) {
    return (
        <span
            style={{
                color: seg.color || TONE_COLOR[seg.tone] || C.grey1,
                fontWeight: EMPHASIZED.has(seg.tone) ? 600 : 400,
            }}
        >
            {seg.text}
        </span>
    );
}

export function HeadlineSentence({ headline, onAction = null }) {
    if (!headline || headline.segments.length === 0) return null;

    // Group consecutive segments by action so a clickable clause is one button
    const groups = [];
    for (const seg of headline.segments) {
        const action = seg.action || null;
        const last = groups[groups.length - 1];
        if (last && last.action === action) last.segs.push(seg);
        else groups.push({ action, segs: [seg] });
    }

    return (
        <div className="mb-7 max-w-5xl">
            <p className="text-3xl leading-snug tracking-tight" style={{ color: C.grey1 }}>
                {groups.map((g, i) =>
                    g.action && onAction ? (
                        <button
                            key={i}
                            onClick={() => onAction(g.action)}
                            className="hover:underline decoration-1 underline-offset-[7px] tracking-tight"
                            style={{ cursor: "pointer", textDecorationColor: "rgba(255,255,255,0.35)" }}
                            title="Jump to details"
                        >
                            {g.segs.map((s, j) => <Seg key={j} seg={s} />)}
                        </button>
                    ) : (
                        g.segs.map((s, j) => <Seg key={`${i}-${j}`} seg={s} />)
                    )
                )}
            </p>
            {headline.context && headline.context.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2.5 text-sm" style={{ color: C.grey2 }}>
                    {headline.context.map((s, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span style={{ color: C.grey3 }}>·</span>}
                            <span>{s.text}</span>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}
